import { create } from 'zustand';
import type { ReactNode } from 'react';
import type { AppToastOptions, ToastVariant } from './types';
import { appendCapped, MAX_TOASTS } from './toast-queue';

// Re-export the queue constants so existing imports from this module keep resolving.
export { appendCapped, MAX_TOASTS };

/**
 * Internal store powering the toast layer. We bypassed `@tamagui/toast`
 * because its `<Toast>` component teleports through a gorhom-style
 * `<PortalItem>` that doesn't preserve React context — the styled toast
 * frame and ALL of its descendants kept losing the Theme context regardless
 * of how many `<Theme>` wrappers we layered on top. Three rounds of fixes
 * later, it was simpler to render the toast in the natural component tree
 * where theme propagation Just Works.
 *
 * FIFO queue (max 3 active) per ADR-0077 / Phase 5. Calling `show` while
 * other toasts are visible appends — the viewport renders all entries
 * stacked. The oldest entry auto-dismisses first via its `expiresAt`
 * timer; queue overflow (4th toast arrives) drops the oldest immediately
 * to keep the cap at 3.
 *
 * `current` is preserved as a convenience alias for code that wants the
 * head-of-queue entry; new viewport code reads `queue` directly.
 */

let nextId = 0;

export interface ActiveToast {
  id: number;
  title: string;
  variant: ToastVariant;
  description?: string | undefined;
  content?: ReactNode | undefined;
  action?: { label: string; onPress: () => void } | undefined;
  /** Auto-dismiss timestamp in ms since epoch, or null for sticky. */
  expiresAt: number | null;
}

interface ToastStoreState {
  /** Currently-visible toasts in arrival order (oldest first). */
  queue: ActiveToast[];
  /** Head-of-queue alias — backwards compatible with the single-toast API. */
  current: ActiveToast | null;
  show: (title: string, opts?: AppToastOptions | undefined) => void;
  /** Dismiss a specific toast by id, or the head when called with no args. */
  hide: (id?: number | undefined) => void;
  /** Drop every queued toast — used by app-wide reset paths (e.g. logout). */
  clear: () => void;
}

export const useToastStore = create<ToastStoreState>()((set) => ({
  queue: [],
  current: null,
  show: (title, opts) => {
    const duration = opts?.duration ?? 4000;
    const toast: ActiveToast = {
      id: ++nextId,
      title,
      variant: opts?.variant ?? 'info',
      description: opts?.description,
      content: opts?.content,
      action: opts?.action,
      expiresAt: duration > 0 ? Date.now() + duration : null,
    };
    set((state) => {
      const next = appendCapped(state.queue, toast);
      return { queue: next, current: next[0] ?? null };
    });
  },
  hide: (id) => {
    set((state) => {
      const next = id == null
        ? state.queue.slice(1) // no id → drop head (backwards-compat)
        : state.queue.filter((t) => t.id !== id);
      return { queue: next, current: next[0] ?? null };
    });
  },
  clear: () => set({ queue: [], current: null }),
}));
